import { plural, t } from '@lingui/core/macro'
import { useRuntimeVariables } from 'app-kit'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import type {
  DocumentMetadataUserEditEntry,
  DocumentMetadataUserEditValues,
  DocumentPageMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { buildDocumentMetadataUserEditPut } from 'features/knowledgeDocumentViewer/utils/buildDocumentMetadataUserEditPut'
import {
  buildDocumentCascadePutsFromPageFamilyEdit,
  type FamilyPageMetadataCascadeMember,
} from 'features/knowledgeDocumentViewer/utils/buildFamilyCrossSurfaceCascadePuts'
import {
  buildDocumentMetadataUserEditPutsForFamily,
  type FamilyDocumentMetadataSaveMember,
} from 'features/knowledgeDocumentViewer/utils/buildFamilyDocumentMetadataUserEditPuts'
import { resolveMetadataUserEditSaveContext } from 'features/knowledgeDocumentViewer/utils/resolveMetadataUserEditSaveContext'
import { seedDocumentMetadataUserEditCaches } from 'features/knowledgeDocumentViewer/utils/seedDocumentMetadataUserEditCaches'
import { useGeckoNotifications } from 'features/notifications'
import { useGeckoPermissions } from 'features/permissions'
import { useCallback } from 'react'
import { useSWRConfig } from 'swr'

export type SaveDocumentMetadataUserEditArgs = {
  bqDocumentId: string
  systemDocument: SystemDocumentEntry | undefined
  priorEdit: DocumentMetadataUserEditEntry | undefined
  userValues: DocumentMetadataUserEditValues
}

export type SaveDocumentMetadataUserEditFamilyArgs = {
  members: readonly FamilyDocumentMetadataSaveMember[]
  userValues: DocumentMetadataUserEditValues
}

export type SaveCascadedDocumentMetadataFromPageFamilyArgs = {
  sharedPageUserValues: DocumentPageMetadataUserEditValues
  members: readonly FamilyPageMetadataCascadeMember[]
}

export function useSaveDocumentMetadataUserEdit(): {
  saveDocumentMetadataUserEdit: (
    args: SaveDocumentMetadataUserEditArgs
  ) => Promise<void>
  saveDocumentMetadataUserEditFamily: (
    args: SaveDocumentMetadataUserEditFamilyArgs
  ) => Promise<void>
  saveCascadedDocumentMetadataFromPageFamily: (
    args: SaveCascadedDocumentMetadataFromPageFamilyArgs
  ) => Promise<void>
} {
  const { rolodexPut } = useRolodexEdit()
  const { siteID, orgID } = useKnowledgeDocumentDataContext()
  const { user } = useGeckoPermissions()
  const { successNotification, errorNotification } = useGeckoNotifications()
  const { mutate } = useSWRConfig()
  const { ROLODEX_URL } = useRuntimeVariables()
  const rolodexLoadEndpoint = `${ROLODEX_URL}/api/v2/entries/load`

  const saveDocumentMetadataUserEdit = useCallback(
    async (args: SaveDocumentMetadataUserEditArgs) => {
      const {
        editedBy,
        siteID: siteId,
        orgID: organizationId,
      } = resolveMetadataUserEditSaveContext({
        userEmail: user?.email,
        siteID,
        orgID,
        surface: 'document',
        errorNotification,
      })

      const put = buildDocumentMetadataUserEditPut({
        siteId,
        organizationId,
        bqDocumentId: args.bqDocumentId,
        editedBy,
        editedAt: new Date().toISOString(),
        systemDocument: args.systemDocument,
        priorEdit: args.priorEdit,
        userValues: args.userValues,
      })

      try {
        await rolodexPut([put])
        successNotification(t`Document metadata updated`, {
          duration: 2,
          placement: 'topRight',
        })
      } catch (error) {
        console.error('Error saving document metadata user edit:', error)
        errorNotification(
          t`Failed to save document metadata. Confirm cantilever hosts exist for this document.`
        )
        throw error
      }
    },
    [
      errorNotification,
      orgID,
      rolodexPut,
      siteID,
      successNotification,
      user?.email,
    ]
  )

  const saveDocumentMetadataUserEditFamily = useCallback(
    async (args: SaveDocumentMetadataUserEditFamilyArgs) => {
      const {
        editedBy,
        siteID: siteId,
        orgID: organizationId,
      } = resolveMetadataUserEditSaveContext({
        userEmail: user?.email,
        siteID,
        orgID,
        surface: 'document',
        errorNotification,
      })
      if (args.members.length === 0) {
        errorNotification(t`No revision family members to save`)
        throw new Error('Empty revision family save')
      }

      const puts = buildDocumentMetadataUserEditPutsForFamily({
        siteId,
        organizationId,
        editedBy,
        editedAt: new Date().toISOString(),
        sharedUserValues: args.userValues,
        members: args.members,
      })

      const revisionCount = args.members.length
      try {
        await rolodexPut(puts)
        successNotification(
          t`Document metadata updated for ${plural(revisionCount, { one: '# revision', other: '# revisions' })}`,
          {
            duration: 2,
            placement: 'topRight',
          }
        )
      } catch (error) {
        console.error(
          'Error saving family document metadata user edits:',
          error
        )
        errorNotification(
          t`Failed to save document metadata for all revisions. Confirm cantilever hosts exist.`
        )
        throw error
      }
    },
    [
      errorNotification,
      orgID,
      rolodexPut,
      siteID,
      successNotification,
      user?.email,
    ]
  )

  const saveCascadedDocumentMetadataFromPageFamily = useCallback(
    async (args: SaveCascadedDocumentMetadataFromPageFamilyArgs) => {
      const {
        editedBy,
        siteID: siteId,
        orgID: organizationId,
      } = resolveMetadataUserEditSaveContext({
        userEmail: user?.email,
        siteID,
        orgID,
        surface: 'document',
        errorNotification,
      })

      const puts = buildDocumentCascadePutsFromPageFamilyEdit({
        siteId,
        organizationId,
        editedBy,
        editedAt: new Date().toISOString(),
        sharedPageUserValues: args.sharedPageUserValues,
        members: args.members,
      })
      if (puts.length === 0) return

      try {
        await rolodexPut(puts)
        await seedDocumentMetadataUserEditCaches({
          endpoint: rolodexLoadEndpoint,
          siteId,
          puts,
          mutate,
        })
      } catch (error) {
        console.error(
          'Error cascading document metadata from page family edit:',
          error
        )
        errorNotification(
          t`Failed to update Document Details for all revisions. Confirm cantilever hosts exist.`
        )
        throw error
      }
    },
    [
      errorNotification,
      mutate,
      orgID,
      rolodexLoadEndpoint,
      rolodexPut,
      siteID,
      user?.email,
    ]
  )

  return {
    saveDocumentMetadataUserEdit,
    saveDocumentMetadataUserEditFamily,
    saveCascadedDocumentMetadataFromPageFamily,
  }
}
